import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { keyframes } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

// Animación de parpadeo retro
const terminalBlink = keyframes`
  0%, 100% { opacity: 1; }
  30% { opacity: 0; }
`;

const ConnectionMonitorTerminal = ({
  socket = null,
  isConnected = false,
  currentTime = new Date(),
  lastHeartbeat = null,
  payload = null,
  defaultCollapsed = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [latency, setLatency] = useState(null);
  const [transport, setTransport] = useState("N/A");
  const [socketId, setSocketId] = useState("N/A");

  // Captura de métricas avanzadas del Socket Engine
  useEffect(() => {
    if (!socket) return;

    const engine = socket.io?.engine;

    const updateTransport = () => {
      if (engine?.transport) {
        setTransport(engine.transport.name.toUpperCase());
      }
    };

    if (socket.connected) {
      setSocketId(socket.id || "N/A");
      updateTransport();
    }

    let pingStart = 0;
    const handlePing = () => {
      pingStart = Date.now();
    };

    const handlePong = () => {
      if (pingStart > 0) {
        setLatency(Date.now() - pingStart);
      }
    };

    if (engine) {
      engine.on("ping", handlePing);
      engine.on("pong", handlePong);
      engine.on("upgrade", updateTransport);
    }

    return () => {
      if (engine) {
        engine.off("ping", handlePing);
        engine.off("pong", handlePong);
        engine.off("upgrade", updateTransport);
      }
    };
  }, [socket, isConnected]);

  const formatTimeOnly = (date) => {
    if (!date) return "--:--:--";
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const themeColor = isConnected ? "#39ff14" : "#ff3b30";
  const borderColor = isConnected
    ? "rgba(57, 255, 20, 0.4)"
    : "rgba(255, 59, 48, 0.4)";

  return (
    <Box
      onClick={() => isCollapsed && setIsCollapsed(false)}
      sx={{
        position: "relative",
        zIndex: 1,
        minWidth: isCollapsed ? { xs: 140, sm: 160 } : { xs: 220, sm: 280 },
        width: "100%",
        maxWidth: isCollapsed ? { xs: 140, sm: 160 } : { xs: 220, sm: 280 },
        bgcolor: "rgba(10, 10, 10, 0.94)",
        border: `1px solid ${themeColor}`,
        borderRadius: 1,
        p: { xs: "6px 10px", sm: "10px 14px" },
        boxShadow: `0 0 15px ${borderColor}`,
        color: themeColor,
        fontFamily: "'Courier New', Courier, monospace",
        fontSize: { xs: "11px", sm: "13px" },
        userSelect: "none",
        overflow: "hidden",
        transition: "all 0.3s ease-in-out",
        cursor: isCollapsed ? "pointer" : "default",

        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.3) 50%)",
          backgroundSize: "100% 4px",
          zIndex: 11,
          pointerEvents: "none",
        },
      }}
    >
      {/* Header de la Terminal */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          borderBottom: `1px solid ${borderColor}`,
          pb: 0.5,
          mb: 0.8,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontFamily: "inherit",
            fontWeight: "bold",
            fontSize: "inherit",
            letterSpacing: 1,
          }}
        >
          [ WeR4U_MONITOR ]
        </Typography>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsCollapsed((prev) => !prev);
          }}
          title={isCollapsed ? "Expandir" : "Contraer"}
          sx={{
            color: themeColor,
            p: 0,
            ml: 1,
            "&:hover": { opacity: 0.8 },
          }}
        >
          {isCollapsed ? (
            <AddIcon fontSize="inherit" />
          ) : (
            <RemoveIcon fontSize="inherit" />
          )}
        </IconButton>
      </Box>

      {/* Contenido en Modo Contraído */}
      {isCollapsed ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontFamily: "inherit", fontSize: "inherit" }}
          >
            SYS STATUS:
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "inherit",
              fontWeight: "bold",
              fontSize: "inherit",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {isConnected ? "ONLINE" : "OFFLINE"}
            <Box
              component="span"
              sx={{
                ml: 0.5,
                animation: `${terminalBlink} 1s infinite`,
              }}
            >
              ■
            </Box>
          </Typography>
        </Box>
      ) : (
        /* Contenido Extendido (Telemetría de Socket + Sistema) */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <RowData
            label="STATUS:"
            value={isConnected ? "CONNECTED" : "DISCONNECTED"}
            isBold
          />
          <RowData label="SYS_CLOCK:" value={formatTimeOnly(currentTime)} />
          <RowData label="HEARTBEAT:" value={formatTimeOnly(lastHeartbeat)} />

          <Box
            sx={{
              pt: 0.5,
              mt: 0.5,
              borderTop: `1px dashed ${borderColor}`,
            }}
          >
            <RowData
              label="SOCKET_ID:"
              value={socketId ? `${socketId.substring(0, 8)}...` : "N/A"}
            />
            <RowData label="TRANSPORT:" value={transport} />
            <RowData
              label="LATENCY:"
              value={latency !== null ? `${latency} ms` : "CALC..."}
            />
          </Box>

          {payload?.profile?.name && (
            <Box
              sx={{
                pt: 0.5,
                mt: 0.5,
                borderTop: `1px dashed ${borderColor}`,
              }}
            >
              <RowData
                label="OPERATOR:"
                value={payload.profile.name.toUpperCase()}
              />
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              textAlign: "right",
              mt: 0.5,
              opacity: 0.6,
              fontSize: "0.7em",
              fontFamily: "inherit",
            }}
          >
            SYSTEM OK{" "}
            <Box
              component="span"
              sx={{ animation: `${terminalBlink} 1s infinite` }}
            >
              _
            </Box>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const RowData = ({ label, value, isBold = false }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
    <Typography
      variant="caption"
      sx={{ fontFamily: "inherit", fontSize: "inherit" }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: isBold ? "bold" : "normal",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default ConnectionMonitorTerminal;
